import { center } from '@turf/center';

import { MAX_SERVICE_LOGO_MARKER_ZOOM } from '../config';

import {
  addHatchToMapboxMap,
  addServiceMarkerToMapboxMap,
} from '@/lib/helpers/mapbox';

import type { Feature, FeatureCollection } from 'geojson';
import type {
  Map,
  FillLayer,
  GeoJSONSourceRaw,
  GeoJSONSource,
  LineLayer,
  SymbolLayer,
} from 'mapbox-gl';

export const areaLayerId = 'area-layer';
export const areaBorderLayerId = 'area-border-layer';
export const invertedAreaLayerId = 'inverted-area-layer';

export function useAreaLayers(
  map: Ref<Map | undefined>,
  area: Ref<Area | undefined>
): void {
  const { theme, logoLightSrc } = useClientStore().clientState;

  const areaSourceId = 'area-source';
  const invertedAreaSourceId = 'inverted-area-source';
  const areaCenterSourceId = 'area-center-source';

  const areaFeature = ref<Feature>();
  const invertedAreaFeature = ref<Feature>();

  const areaCenterFeature = computed(() => {
    return areaFeature.value ? center(areaFeature.value) : null;
  });

  async function updateAreaFeatures(): Promise<void> {
    addHatchToMapboxMap(map, { color: theme.colorNeutralDark3 });

    await addServiceMarkerToMapboxMap(map, {
      color: theme.colorPrimary,
      url: logoLightSrc,
    });

    areaFeature.value = area.value
      ? {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [area.value.coordinates],
          },
          properties: {
            id: area.value.id,
          },
        }
      : undefined;

    invertedAreaFeature.value = area.value
      ? {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [
              // Polygon that covers the whole world
              [
                [-180, -90],
                [-180, 90],
                [180, 90],
                [180, -90],
                [-180, -90],
              ],
              area.value.coordinates,
            ],
          },
          properties: {
            id: area.value.id,
          },
        }
      : undefined;
  }

  function syncAreaSources(): void {
    if (!map.value || !area.value) return;

    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: areaFeature.value ? [areaFeature.value] : [],
      },
    };

    if (!map.value.getSource(areaSourceId)) {
      map.value.addSource(areaSourceId, source);
    } else {
      (map.value.getSource(areaSourceId) as GeoJSONSource)?.setData(
        source.data as FeatureCollection
      );
    }

    const invertedSource: GeoJSONSourceRaw = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: invertedAreaFeature.value ? [invertedAreaFeature.value] : [],
      },
    };

    if (!map.value.getSource(invertedAreaSourceId)) {
      map.value.addSource(invertedAreaSourceId, invertedSource);
    } else {
      (map.value.getSource(invertedAreaSourceId) as GeoJSONSource)?.setData(
        invertedSource.data as FeatureCollection
      );
    }

    const centerSource: GeoJSONSourceRaw = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: areaCenterFeature.value ? [areaCenterFeature.value] : [],
      },
    };
    if (!map.value.getSource(areaCenterSourceId)) {
      map.value.addSource(areaCenterSourceId, centerSource);
    }
  }

  function registerLayers(): void {
    if (!map.value) return;

    const areaLayer: FillLayer = {
      id: areaLayerId,
      type: 'fill',
      source: areaSourceId,
      paint: {
        'fill-opacity': {
          stops: [
            [8, 0.2],
            [9, 0.05],
            [12, 0.02],
            [13, 0],
          ],
        },
        'fill-color': theme.colorPrimary,
      },
      minzoom: 0,
      maxzoom: 12,
    };

    if (!map.value.getLayer(areaLayerId)) {
      map.value.addLayer(areaLayer);
    }

    const invertedAreaLayer: FillLayer = {
      id: invertedAreaLayerId,
      type: 'fill',
      source: invertedAreaSourceId,
      paint: {
        'fill-pattern': 'hatch',
        'fill-opacity': {
          stops: [
            [8, 0.1],
            [10, 0.3],
            [12, 0.5],
          ],
        },
      },
      minzoom: 8,
    };
    if (!map.value.getLayer(invertedAreaLayerId)) {
      map.value.addLayer(invertedAreaLayer);
    }

    const areaBorderLayer: LineLayer = {
      id: areaBorderLayerId,
      type: 'line',
      source: areaSourceId,

      paint: {
        'line-width': 1,
        'line-opacity': 0.2,
        'line-color': theme.colorPrimary,
      },
      minzoom: 0,
    };

    if (!map.value.getLayer(areaBorderLayerId)) {
      map.value.addLayer(areaBorderLayer);
    }

    const areaCenterLayerId = 'area-center-layer';

    const areaCenterLayer: SymbolLayer = {
      id: areaCenterLayerId,
      type: 'symbol',
      source: areaCenterSourceId,
      layout: {
        'icon-image': 'service-logo',
        'icon-size': {
          stops: [
            [0, 0.3],
            [10, 0.45],
          ],
        },
        'icon-ignore-placement': true,
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom-left',
      },
      minzoom: 0,
      maxzoom: MAX_SERVICE_LOGO_MARKER_ZOOM,
    };
    if (!map.value.getLayer(areaCenterLayerId)) {
      map.value.addLayer(areaCenterLayer);
    }
  }

  watchImmediate(area, async () => {
    await until(map).not.toBeUndefined();

    await updateAreaFeatures();
    syncAreaSources();

    registerLayers();
  });
}
